import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../contexts/UserContext';
import { useMetaTags } from '../../hooks/useMetaTags';
import { api } from '../../utils/apiClient';

function VerifyEmailChange() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn, logout } = useUser();
  
  // Token from URL
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token');
  
  // UI state
  const [isVerifying, setIsVerifying] = useState(true);
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  // Set meta tags
  useMetaTags(
    t('account.verifyEmail', 'Verify Email Address'),
    t('account.verifyEmailDescription', 'Verify your new email address')
  );
  
  // Verify token when component loads
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setIsVerifying(false);
        setErrorMessage(t('auth.missingToken', 'Email verification token is missing'));
        return;
      }
      
      try {
        const response = await api.auth.verifyEmailChange(token);
        
        setIsVerifying(false);
        
        if (response.success) {
          setVerificationSuccess(true);
          // If user is logged in, log them out since their email changed
          if (isLoggedIn) {
            logout();
          }
        } else {
          setErrorMessage(response.message || t('auth.invalidToken', 'Email verification token is invalid or has expired'));
        }
      } catch (error) {
        console.error('Email verification error:', error);
        setIsVerifying(false);
        setErrorMessage(t('auth.verificationFailed', 'Failed to verify email address'));
      }
    };
    
    verifyToken();
  }, [token, t, isLoggedIn, logout]);
  
  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-12 col-md-8 col-lg-6">
          <div className="card shadow-sm">
            <div className="card-body p-4">
              <h1 className="h4 mb-4 text-center">{t('account.verifyEmail', 'Verify Email Address')}</h1>
              
              {isVerifying ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">{t('common.loading', 'Loading...')}</span>
                  </div>
                  <p className="mt-3">{t('account.verifyingEmail', 'Verifying your email address...')}</p>
                </div>
              ) : verificationSuccess ? (
                <div className="alert alert-success" role="alert">
                  <h5 className="alert-heading">{t('account.emailVerified', 'Email Address Verified!')}</h5>
                  <p>{t('account.emailVerifiedMessage', 'Your email address has been successfully updated. You can now log in with your new email address.')}</p>
                  <div className="mt-3">
                    <Link to="/login" className="btn btn-primary">
                      {t('auth.login', 'Login')}
                    </Link>
                  </div>
                </div>
              ) : (
                <div className="alert alert-danger" role="alert">
                  <h5 className="alert-heading">{t('account.verificationFailed', 'Verification Failed')}</h5>
                  <p>{errorMessage}</p>
                  <div className="mt-3">
                    <Link to="/account" className="btn btn-primary">
                      {t('account.goToAccount', 'Go to Account Settings')}
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VerifyEmailChange; 